import React from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth, useBadgeMap } from "@/lib/auth-context";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { LogOut, Edit3, Award, Save, User, Megaphone, Mic, Vote, Calendar, Info } from "lucide-react";
import SchoolSelect from "@/components/site/SchoolSelect";
import JoinCefBlock from "@/components/site/JoinCefBlock";
import PageBlocks from "@/components/site/PageBlocks";

const ENGAGEMENT = [
  { id: "infos", label: "Être tenu·e informé·e" },
  { id: "creer_action", label: "Créer une action" },
  { id: "infos_region", label: "Recevoir les infos de ma région" },
  { id: "engagement_libre", label: "M'engager, peu importe la forme" },
];

const TYPES_ENSEIGNEMENT = ["Général", "Technique de transition", "Technique de qualification", "Professionnel", "Artistique", "Spécialisé", "CEFA"];
const ANNEES = ["1e", "2e", "3e", "4e", "5e", "6e", "7e"];

const TABS = [
  { id: "profil", label: "Mon profil", Icon: User },
  { id: "participations", label: "Mes participations", Icon: Calendar },
  { id: "actions", label: "Mes actions", Icon: Megaphone },
  { id: "temoignages", label: "Mes témoignages", Icon: Mic },
  { id: "consultations", label: "Mes consultations", Icon: Vote },
  { id: "badges", label: "Mes badges", Icon: Award },
  { id: "cef", label: "Le CEF", Icon: Info },
];

export default function Profil() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "profil";
  const setTab = (t) => setParams({ tab: t });
  const [activity, setActivity] = React.useState(null);

  React.useEffect(() => {
    if (!loading && !user) nav("/login");
  }, [user, loading, nav]);

  React.useEffect(() => {
    if (user) api.get("/me/activity").then((r) => setActivity(r.data)).catch(() => {});
  }, [user]);

  if (loading || !user) return <div className="max-w-3xl mx-auto px-4 py-12">Chargement…</div>;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <PageBlocks slug="profil" className="mb-6" />
      <div className="neo-card p-5 sm:p-6 bg-white flex items-start gap-4 flex-wrap" data-testid="profile-header">
        <div className="w-16 h-16 sm:w-20 sm:h-20 bg-cef-yellow border-2 border-black rounded-md flex items-center justify-center font-display font-black text-2xl sm:text-3xl shrink-0 overflow-hidden">
          {user.picture ? <img src={user.picture} alt={user.name} className="w-full h-full object-cover" /> : (user.name || "?")[0].toUpperCase()}
        </div>
        <div className="flex-1 min-w-[200px]">
          <h1 className="font-display font-black text-2xl sm:text-3xl tracking-tighter">{user.name} {user.last_name || ""}</h1>
          <div className="text-xs text-neutral-600">{user.email}</div>
          <div className="mt-1 text-xs flex flex-wrap gap-2">
            {user.school_name && <span className="chip bg-cef-paper">🏫 {user.school_name}</span>}
            {user.province && <span className="chip bg-cef-paper">📍 {user.province}</span>}
            {user.is_delegue && <span className="chip bg-cef-blue text-white">🎖️ Délégué·e</span>}
            {(user.badges || []).length > 0 && <span className="chip bg-cef-yellow">🏅 {(user.badges || []).length} badge·s</span>}
          </div>
        </div>
        <button onClick={() => { logout(); nav("/"); }} className="neo-btn bg-white text-sm py-2" data-testid="logout-btn"><LogOut className="w-4 h-4" /> Déconnexion</button>
      </div>

      <div className="mt-6 flex flex-wrap gap-2" data-testid="profile-tabs">
        {TABS.map(({ id, label, Icon }) => (
          <button key={id} onClick={() => setTab(id)} className={`chip inline-flex items-center gap-1.5 ${tab === id ? "bg-black text-white" : "bg-white"}`} data-testid={`profile-tab-${id}`}>
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {tab === "profil" && <ProfilTab user={user} />}
        {tab === "participations" && <ParticipationsTab activity={activity} />}
        {tab === "actions" && <ActionsTab initiatives={activity?.initiatives || []} joined={activity?.joined_initiatives || []} />}
        {tab === "temoignages" && <TemoignagesTab list={activity?.testimonials || []} />}
        {tab === "consultations" && <ConsultationsTab list={activity?.consultations || []} />}
        {tab === "badges" && <BadgesTab user={user} />}
        {tab === "cef" && <CefTab />}
      </div>
    </div>
  );
}

function ProfilTab({ user }) {
  const { refresh } = useAuth();
  const [editing, setEditing] = React.useState(false);
  const [form, setForm] = React.useState({
    name: user.name || "", last_name: user.last_name || "", age: user.age || "",
    city: user.city || "", province: user.province || "",
    school_id: user.school_id || "", school_name: user.school_name || "",
    school_commune: user.school_commune || "", school_code_postal: user.school_code_postal || "",
    type_enseignement: user.type_enseignement || user.niveau_etudes || "",
    annee: user.annee || "", is_delegue: !!user.is_delegue,
    can_be_recontacted: !!user.can_be_recontacted, relais_local_optin: !!user.relais_local_optin,
    engagement_types: user.engagement_types || [],
  });

  const toggle = (i) => setForm((f) => ({ ...f, engagement_types: f.engagement_types.includes(i) ? f.engagement_types.filter((x) => x !== i) : [...f.engagement_types, i] }));

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.patch("/auth/user/me", { ...form, age: form.age ? parseInt(form.age) : null });
      toast.success("Profil mis à jour");
      setEditing(false);
      refresh();
    } catch { toast.error("Erreur"); }
  };

  if (!editing) {
    return (
      <div className="neo-card p-6 bg-white" data-testid="profil-view">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-black text-xl tracking-tighter uppercase">Informations</h2>
          <button onClick={() => setEditing(true)} className="neo-btn bg-cef-yellow text-sm py-2" data-testid="edit-profile-btn"><Edit3 className="w-4 h-4" /> Éditer</button>
        </div>
        <dl className="grid sm:grid-cols-2 gap-3 text-sm">
          <Field label="Prénom" value={user.name} />
          <Field label="Nom" value={user.last_name} />
          <Field label="Email" value={user.email} />
          <Field label="Âge" value={user.age} />
          <Field label="École" value={user.school_name} />
          <Field label="Commune" value={user.school_commune} />
          <Field label="Province" value={user.province} />
          <Field label="Type d'enseignement" value={user.type_enseignement || user.niveau_etudes} />
          <Field label="Année" value={user.annee && `${user.annee} secondaire`} />
          <Field label="Délégué·e" value={user.is_delegue ? "Oui" : "Non"} />
          <Field label="Relais local" value={user.relais_local_optin ? "Oui 📍" : "Non"} />
          <Field label="Recontact actions" value={user.can_be_recontacted ? "Oui" : "Non"} />
        </dl>
      </div>
    );
  }

  return (
    <form onSubmit={save} className="neo-card p-6 bg-white grid sm:grid-cols-2 gap-3" data-testid="profile-edit-form">
      <input className="neo-input" placeholder="Prénom" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
      <input className="neo-input" placeholder="Nom" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
      <input className="neo-input" type="number" placeholder="Âge" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} />
      <input className="neo-input" placeholder="Ville" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
      <div className="sm:col-span-2 font-bold text-sm uppercase">Mon école</div>
      <SchoolSelect
        value={{ province: form.province, school_code_postal: form.school_code_postal, school_id: form.school_id, school_name: form.school_name, school_commune: form.school_commune }}
        onChange={(v) => setForm({ ...form, ...v })}
        testIdPrefix="profil-"
      />
      <select className="neo-input" value={form.type_enseignement} onChange={(e) => setForm({ ...form, type_enseignement: e.target.value })}>
        <option value="">Type d'enseignement</option>
        {TYPES_ENSEIGNEMENT.map((n) => <option key={n}>{n}</option>)}
      </select>
      <select className="neo-input" value={form.annee} onChange={(e) => setForm({ ...form, annee: e.target.value })}>
        <option value="">Année</option>
        {ANNEES.map((a) => <option key={a} value={a}>{a} secondaire</option>)}
      </select>
      <div className="sm:col-span-2 neo-card p-3 bg-cef-paper">
        <div className="font-bold text-sm uppercase mb-2">Es-tu délégué·e ?</div>
        <div className="flex gap-2">
          <button type="button" onClick={() => setForm({ ...form, is_delegue: true })} className={`neo-btn text-sm py-2 ${form.is_delegue ? "bg-cef-blue text-white" : "bg-white"}`}>Oui</button>
          <button type="button" onClick={() => setForm({ ...form, is_delegue: false })} className={`neo-btn text-sm py-2 ${!form.is_delegue ? "bg-cef-pink text-white" : "bg-white"}`}>Non</button>
        </div>
      </div>
      <div className="sm:col-span-2">
        <div className="font-bold text-sm uppercase mb-2">Mes engagements</div>
        <div className="flex flex-wrap gap-2">
          {ENGAGEMENT.map((e) => (
            <button key={e.id} type="button" onClick={() => toggle(e.id)} className={`chip ${form.engagement_types?.includes(e.id) ? "bg-cef-pink text-white" : "bg-white"}`}>{e.label}</button>
          ))}
        </div>
      </div>
      <label className="sm:col-span-2 flex items-start gap-2 text-sm cursor-pointer">
        <input type="checkbox" className="mt-1" checked={form.can_be_recontacted} onChange={(e) => setForm({ ...form, can_be_recontacted: e.target.checked })} />
        <span>Recontact pour actions locales</span>
      </label>
      <label className="sm:col-span-2 flex items-start gap-2 text-sm cursor-pointer">
        <input type="checkbox" className="mt-1" checked={form.relais_local_optin} onChange={(e) => setForm({ ...form, relais_local_optin: e.target.checked })} />
        <span>Devenir relais local 📍</span>
      </label>
      <div className="sm:col-span-2 flex gap-2">
        <button type="submit" className="neo-btn bg-cef-pink text-white flex-1" data-testid="save-profile-btn"><Save className="w-4 h-4" /> Enregistrer</button>
        <button type="button" onClick={() => setEditing(false)} className="neo-btn bg-white">Annuler</button>
      </div>
    </form>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <dt className="text-xs uppercase font-bold tracking-tight text-neutral-500">{label}</dt>
      <dd className="font-bold">{value || <span className="text-neutral-400">—</span>}</dd>
    </div>
  );
}

function ParticipationsTab({ activity }) {
  if (!activity) return <div className="text-sm text-neutral-500">Chargement…</div>;
  return (
    <div className="grid sm:grid-cols-4 gap-4" data-testid="participations-tab">
      <Stat n={activity.initiatives?.length || 0} label="Actions créées" color="bg-cef-pink text-white" />
      <Stat n={activity.joined_initiatives?.length || 0} label="Actions rejointes" color="bg-cef-blue text-white" />
      <Stat n={activity.testimonials?.length || 0} label="Témoignages publiés" color="bg-cef-green" />
      <Stat n={activity.consultations?.length || 0} label="Consultations votées" color="bg-cef-yellow" />
      <div className="sm:col-span-4 neo-card p-5 bg-white">
        <div className="font-bold text-lg">Total : {activity.participations_count} participation·s</div>
        <p className="text-sm text-neutral-600 mt-1">Continue à voter, témoigner, créer pour gagner des badges et faire bouger les choses 🚀</p>
      </div>
    </div>
  );
}

function Stat({ n, label, color }) {
  return (
    <div className={`neo-card p-5 ${color}`}>
      <div className="font-display font-black text-4xl tracking-tighter">{n}</div>
      <div className="text-sm font-bold uppercase tracking-tight mt-1">{label}</div>
    </div>
  );
}

function ActionsTab({ initiatives, joined }) {
  const empty = !initiatives.length && !joined.length;
  if (empty) return <div className="neo-card p-5 bg-white text-neutral-500" data-testid="actions-tab-empty">Tu n'as pas encore d'action. <Link to="/carte" className="font-bold underline">Découvre la carte ↗</Link></div>;
  return (
    <div className="grid gap-8" data-testid="actions-tab">
      {initiatives.length > 0 && (
        <section data-testid="my-created-actions">
          <h3 className="font-display font-black text-lg uppercase tracking-tight mb-3">Mes actions créées</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            {initiatives.map((i) => (
              <div key={i.id} className="neo-card p-4 bg-white" data-testid={`my-action-${i.id}`}>
                <h3 className="font-display font-black text-base tracking-tight">{i.name}</h3>
                <div className="text-xs text-neutral-500 mt-1">{[i.school, i.city, i.province].filter(Boolean).join(" · ")}</div>
                <p className="mt-2 text-sm line-clamp-3">{i.description}</p>
                {i.date && <div className="mt-2 text-xs"><Calendar className="w-3 h-3 inline mr-1" /> {new Date(i.date).toLocaleDateString("fr-BE")}</div>}
              </div>
            ))}
          </div>
        </section>
      )}
      {joined.length > 0 && (
        <section data-testid="my-joined-actions">
          <h3 className="font-display font-black text-lg uppercase tracking-tight mb-3">Actions auxquelles je participe</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            {joined.map((i) => (
              <div key={i.id} className="neo-card p-4 bg-cef-paper" data-testid={`my-joined-${i.id}`}>
                <h3 className="font-display font-black text-base tracking-tight">{i.name}</h3>
                <div className="text-xs text-neutral-500 mt-1">{[i.school, i.city, i.province].filter(Boolean).join(" · ")}</div>
                <p className="mt-2 text-sm line-clamp-3">{i.description}</p>
                {i.date && <div className="mt-2 text-xs"><Calendar className="w-3 h-3 inline mr-1" /> {new Date(i.date).toLocaleDateString("fr-BE")}</div>}
                {i.contact && <a href={`mailto:${i.contact}`} className="mt-2 inline-block text-xs font-bold underline">Contacter l'organisateur·rice ↗</a>}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function TemoignagesTab({ list }) {
  if (!list?.length) return <div className="neo-card p-5 bg-white text-neutral-500" data-testid="temoignages-tab-empty">Aucun témoignage publié. <Link to="/temoignages" className="font-bold underline">Partage ton vécu ↗</Link></div>;
  return (
    <div className="grid gap-3" data-testid="temoignages-tab">
      {list.map((t) => (
        <div key={t.id} className="neo-card p-4 bg-white" data-testid={`my-temoignage-${t.id}`}>
          <p className="text-sm italic">« {t.text} »</p>
          <div className="mt-2 text-xs text-neutral-500">{t.format} · {t.theme || ""} · {new Date(t.created_at).toLocaleDateString("fr-BE")} {t.approved ? "" : "· en modération"}</div>
        </div>
      ))}
    </div>
  );
}

function ConsultationsTab({ list }) {
  if (!list?.length) return <div className="neo-card p-5 bg-white text-neutral-500" data-testid="consultations-tab-empty">Aucune consultation votée. <Link to="/donner-mon-avis" className="font-bold underline">Donne ton avis ↗</Link></div>;
  return (
    <div className="grid gap-3" data-testid="consultations-tab">
      {list.map((r) => (
        <div key={r.id} className="neo-card p-4 bg-white" data-testid={`my-cons-${r.id}`}>
          <div className="font-bold text-sm">{r.consultation?.title || "Consultation"}</div>
          <div className="text-xs text-neutral-500 mt-1">Ta réponse : <strong>{r.answer}</strong></div>
          {r.extra_text && <p className="mt-2 text-sm italic">« {r.extra_text} »</p>}
          {r.consultation?.slug && <Link to={`/donner-mon-avis/${r.consultation.slug}`} className="text-xs font-bold underline mt-2 inline-block">Voir les résultats →</Link>}
        </div>
      ))}
    </div>
  );
}

function BadgesTab({ user }) {
  const badgeMap = useBadgeMap();
  const earned = user.badges || [];
  const all = Object.values(badgeMap);
  const earnedB = all.filter((b) => earned.includes(b.slug));
  const lockedB = all.filter((b) => !earned.includes(b.slug));
  return (
    <div data-testid="badges-tab">
      <div className="font-bold uppercase text-sm tracking-tight mb-3">Débloqués ({earnedB.length}/{all.length})</div>
      <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3" data-testid="badges-earned">
        {earnedB.map((b) => (
          <div key={b.slug} className={`neo-card p-4 ${b.color}`} data-testid={`badge-${b.slug}`}>
            <div className="text-3xl">{b.emoji}</div>
            <div className="mt-1 font-display font-black text-base tracking-tight">{b.label}</div>
            <p className="text-xs mt-1 opacity-90">{b.description}</p>
          </div>
        ))}
        {earnedB.length === 0 && <div className="neo-card p-5 bg-white text-neutral-500 sm:col-span-2 md:col-span-3">Pas encore de badge. Vote, témoigne, ajoute une action pour en gagner !</div>}
      </div>
      {lockedB.length > 0 && (
        <div className="mt-6">
          <div className="font-bold uppercase text-sm tracking-tight mb-3 text-neutral-500">À débloquer</div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3" data-testid="badges-locked">
            {lockedB.map((b) => (
              <div key={b.slug} className="neo-card p-4 bg-white opacity-60" data-testid={`badge-locked-${b.slug}`}>
                <div className="text-3xl grayscale">{b.emoji}</div>
                <div className="mt-1 font-display font-bold text-sm tracking-tight">{b.label}</div>
                <p className="text-xs mt-1 text-neutral-600">{b.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CefTab() {
  return (
    <div className="grid gap-4" data-testid="cef-tab">
      <div className="neo-card p-6 bg-white">
        <h2 className="font-display font-black text-2xl tracking-tighter uppercase">Le CEF</h2>
        <p className="mt-3 text-sm leading-relaxed">
          Le <strong>Comité des Élèves Francophones (CEF)</strong> est l'organisation officielle qui représente les élèves du secondaire en Fédération Wallonie-Bruxelles depuis 1986.
          Nous travaillons avec la ministre, les écoles et les autres acteurs de l'éducation pour porter la voix des élèves dans toutes les décisions qui les concernent.
        </p>
        <div className="mt-4 flex flex-wrap gap-3">
          <Link to="/cef" className="neo-btn bg-cef-blue text-white">En savoir plus</Link>
        </div>
      </div>
      <JoinCefBlock variant="yellow" />
    </div>
  );
}
